import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Flag, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { GOAL_STATUS_ES, labelStatus } from "@/lib/renata/experience/status-labels";
import { toast } from "sonner";

export const Route = createFileRoute("/app/renata/goals")({
  component: GoalsPage,
  head: () => ({ meta: [{ title: "Objetivos — Renata" }] }),
});

function GoalsPage() {
  const tenantId = useWorkspaceTenantId();
  const [goals, setGoals] = useState<Array<Record<string, unknown>>>([]);
  const [instances, setInstances] = useState<Array<Record<string, unknown>>>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    if (!tenantId) return;
    const res = await fetch("/api/renata/planning?view=goals", {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    });
    if (!res.ok) {
      toast.error("No se pudieron cargar los objetivos");
      return;
    }
    const d = await res.json();
    setGoals(d.goals ?? []);
    setInstances(d.instances ?? []);
  }

  useEffect(() => {
    void load();
  }, [tenantId]);

  const activeGoalIds = useMemo(() => {
    return new Set(
      instances
        .filter((i) => String(i.status) === "active")
        .map((i) => String(i.goal_id ?? i.goalId ?? "")),
    );
  }, [instances]);

  async function activate(goalId: string) {
    if (!tenantId || busyId) return;
    if (activeGoalIds.has(goalId)) {
      toast.message("Ese objetivo ya está en curso");
      return;
    }
    setBusyId(goalId);
    try {
      const res = await fetch("/api/renata/planning", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ action: "goal_create", goalId }),
      });
      const d = await res.json();
      if (!res.ok) {
        toast.error(String(d.error ?? "No se pudo activar"));
        return;
      }
      toast.success("Objetivo activado. El avance empieza en 0% y sube cuando completes acciones del plan.");
      await load();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
      <RenataPageChrome
        screenId="goals"
        title="Objetivos"
        subtitle="Elegí un resultado de negocio. Renata lo conecta con planes y acciones — vos aprobás."
      />

      <div className="rounded-lg border border-border bg-card p-3 text-xs text-foreground/85 space-y-1">
        <p><strong>Qué hace “Activar”:</strong> crea un objetivo en curso para tu negocio (no vende ni cambia precios solo).</p>
        <p><strong>Progreso %:</strong> arranca en 0%. Sube cuando avanzás misiones/acciones vinculadas. No es automático.</p>
        <p><strong>Tu siguiente paso:</strong> activá uno → abrí Planes de trabajo o el Planificador para armar el camino.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {goals.map((g) => {
          const id = String(g.id);
          const already = activeGoalIds.has(id);
          return (
            <div key={id} className="rounded-xl border p-4 space-y-2">
              <h2 className="font-medium text-sm">{String(g.title)}</h2>
              <p className="text-xs text-foreground/80">{String(g.description)}</p>
              <p className="text-[11px] text-foreground/75">Meta orientativa: {String(g.defaultTargetHint)}</p>
              <Button
                size="sm"
                variant={already ? "secondary" : "outline"}
                disabled={already || busyId === id}
                onClick={() => void activate(id)}
              >
                {busyId === id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                {already ? "Ya en curso" : "Activar objetivo"}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-semibold">En curso</h2>
        {instances.length === 0 && (
          <p className="text-sm text-foreground/80">Todavía no hay objetivos activos. Activá uno arriba.</p>
        )}
        {instances.map((i) => (
          <div key={String(i.id)} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
            <Flag className="h-4 w-4 text-violet-600 shrink-0" />
            <span className="font-medium flex-1 truncate">{String(i.title)}</span>
            <Badge variant="secondary">{labelStatus(GOAL_STATUS_ES, String(i.status))}</Badge>
            <span className="text-xs tabular-nums text-muted-foreground" title="Avanza con acciones del plan">
              Avance {Number(i.progress)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
